import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EmptyState, PageHeader, StatusPill } from "../components/primitives";
import { IconBolt } from "../components/icons";
import {
  ApiError, WEEKDAYS, createAutomation, deleteAutomation, fetchAutomations, fetchTemplates,
  runAutomationNow, updateAutomation,
  type Automation, type AutomationInput, type Template,
} from "../lib/api-automations";
import type { DeliveryStatus } from "../lib/api-broadcasts";
import { ammanStamp } from "../lib/dates";
import { SetupChecklist, TopicPicker, useTopicLabel } from "./BroadcastsPage";

/**
 * Automations: newsletters that build and send themselves.
 *
 * "Every Sunday at 09:00, build <template> with the newest articles for each
 * topic, and send it to that topic's followers." Each run becomes a broadcast,
 * so every issue — automatic or not — is in Broadcasts with its results.
 */
export function AutomationsPage() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState<Automation[] | null>(null);
  const [delivery, setDelivery] = useState<DeliveryStatus>();
  const [error, setError] = useState<ApiError | null>(null);
  // Arriving from Design Studio's "Automate weekly" opens the form on that template.
  const [editing, setEditing] = useState<Automation | "new" | null>(params.get("template") ? "new" : null);

  const load = () => {
    fetchAutomations()
      .then(({ data, meta }) => { setRows(data); setDelivery(meta.delivery); })
      .catch((e: ApiError) => setError(e));
  };
  useEffect(load, []);

  return (
    <>
      <PageHeader
        title="Automations"
        meta="Newsletters that build and send themselves, every week."
        actions={editing ? undefined : (
          <button type="button" className="nl-btn-primary" onClick={() => setEditing("new")}>New automation</button>
        )}
      />
      <SetupChecklist delivery={delivery} />

      {editing && (
        <AutomationForm
          existing={editing === "new" ? null : editing}
          initialTemplate={params.get("template") ?? ""}
          onDone={() => { setEditing(null); load(); }}
        />
      )}

      {error ? (
        <EmptyState
          title={error.pendingMigration ? "Automations are waiting for a database update" : "Automations could not be loaded"}
          body={error.message}
          hint={error.pendingMigration ? `Run ${error.pendingMigration} in Supabase, then reload.` : "Reload the page to try again."}
        />
      ) : rows === null ? (
        <p className="bc-loading">Loading automations…</p>
      ) : rows.length === 0 && !editing ? (
        <EmptyState
          icon={<IconBolt width={22} height={22} />}
          title="No automations yet"
          body="Pick a template from Design Studio, the topics and a day, and the newsletter builds itself every week from the newest articles."
          hint="You choose whether each issue waits for your approval or goes out on its own."
        />
      ) : (
        <ul className="au-list">
          {rows.map((a) => <AutomationRow key={a.id} a={a} onEdit={() => setEditing(a)} onChange={load} />)}
        </ul>
      )}
    </>
  );
}

function describe(a: Pick<Automation, "weekday" | "send_time" | "approval">): string {
  return `Every ${WEEKDAYS[a.weekday]} at ${a.send_time} (Amman) · ${a.approval === "auto" ? "sends automatically" : "waits for your approval"}`;
}

const RUN_PILL: Record<string, { tone: "success" | "review" | "active" | "neutral" | "error"; label: string }> = {
  scheduled: { tone: "success", label: "Sent" },
  created: { tone: "review", label: "Waiting" },
  skipped: { tone: "neutral", label: "Skipped" },
  failed: { tone: "error", label: "Failed" },
};

function AutomationRow({ a, onEdit, onChange }: { a: Automation; onEdit: () => void; onChange: () => void }) {
  const navigate = useNavigate();
  const topic = useTopicLabel();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const active = a.status === "active";

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setNote(null);
    try { await fn(); onChange(); } catch (e) { setNote((e as Error).message); } finally { setBusy(false); }
  };
  const buildNow = async () => {
    setBusy(true);
    setNote(null);
    try {
      const { data } = await runAutomationNow(a.id);
      if (data.campaign_id) navigate(`/broadcasts/${data.campaign_id}`);
      else { setNote(data.detail); onChange(); }
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="au-row">
      <div className="au-main">
        <div className="au-title">
          <h2>{a.name}</h2>
          <StatusPill tone={active ? "active" : "neutral"}>{active ? "Active" : "Paused"}</StatusPill>
        </div>
        <p className="au-what">{describe(a)}</p>
        <p className="au-meta">
          Builds <strong>{a.template_title ?? "a deleted template"}</strong> for{" "}
          <span lang="ar" dir="rtl">{a.categories.map(topic).join("، ")}</span>
          {active && a.next_run_at && <> · Next: {ammanStamp(a.next_run_at)}</>}
        </p>
        {a.runs.length > 0 && (
          <ol className="au-runs" aria-label="Recent runs">
            {a.runs.map((r) => {
              const pill = RUN_PILL[r.status] ?? RUN_PILL.created;
              return (
                <li key={r.ran_at}>
                  <span className="tabnum au-run-when">{ammanStamp(r.ran_at)}</span>
                  <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
                  <span className="au-run-detail">{r.detail}</span>
                  {r.campaign_id && <Link className="nl-text-btn" to={`/broadcasts/${r.campaign_id}`}>Open</Link>}
                </li>
              );
            })}
          </ol>
        )}
        {note && <p className="bc-hint" role="status">{note}</p>}
      </div>
      <div className="au-actions">
        <button type="button" className="nl-btn-secondary" disabled={busy}
          onClick={() => act(() => updateAutomation(a.id, { status: active ? "paused" : "active" }))}>
          {active ? "Pause" : "Resume"}
        </button>
        <button type="button" className="nl-btn-secondary" disabled={busy} onClick={buildNow}
          title="Build this week's issue now as a draft, to check it">
          Build now
        </button>
        <button type="button" className="nl-text-btn" disabled={busy} onClick={onEdit}>Edit</button>
        <button type="button" className="nl-text-btn ds-remove" disabled={busy}
          onClick={() => { if (window.confirm(`Delete "${a.name}"? Issues it already sent stay in Broadcasts.`)) void act(() => deleteAutomation(a.id)); }}>
          Delete
        </button>
      </div>
    </li>
  );
}

function AutomationForm({ existing, initialTemplate, onDone }: {
  existing: Automation | null; initialTemplate: string; onDone: () => void;
}) {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [form, setForm] = useState<AutomationInput>(() => existing ? {
    name: existing.name, template_id: existing.template_id, categories: existing.categories,
    subject: existing.subject, weekday: existing.weekday, send_time: existing.send_time, approval: existing.approval,
  } : {
    name: "Weekly newsletter", template_id: initialTemplate, categories: [], subject: "",
    weekday: 0, send_time: "09:00", approval: "required",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof AutomationInput>(k: K, v: AutomationInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    fetchTemplates().then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
  }, []);
  // A template brings its topics and subject, until they are changed here.
  useEffect(() => {
    if (existing || !templates) return;
    const t = templates.find((x) => x.id === form.template_id);
    if (!t) return;
    setForm((f) => ({
      ...f,
      categories: f.categories.length ? f.categories : t.categories,
      subject: f.subject || t.subject,
    }));
  }, [form.template_id, templates, existing]);


  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (existing) await updateAutomation(existing.id, form);
      else await createAutomation({ ...form, status: "active" });
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const ready = form.name.trim() && form.template_id && form.categories.length && form.subject.trim() && form.send_time;

  return (
    <section className="bc-new au-form" aria-label={existing ? "Edit automation" : "New automation"}>
      <div className="au-form-grid">
        <label className="bc-field">
          <span>Name</span>
          <input className="nl-input" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </label>
        <label className="bc-field">
          <span>Template</span>
          <select className="nl-builder-select" value={form.template_id} onChange={(e) => set("template_id", e.target.value)}>
            <option value="">{templates === null ? "Loading…" : templates.length ? "Choose a template" : "No templates yet"}</option>
            {(templates ?? []).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
          {templates?.length === 0 && <em className="bc-hint">Add one in <Link to="/design">Design Studio</Link> first.</em>}
        </label>
        <label className="bc-field au-wide">
          <span>Subject line</span>
          <input className="nl-input" value={form.subject} onChange={(e) => set("subject", e.target.value)} dir="auto" />
        </label>
        <label className="bc-field">
          <span>Day</span>
          <select className="nl-builder-select" value={form.weekday} onChange={(e) => set("weekday", Number(e.target.value))}>
            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </label>
        <label className="bc-field">
          <span>Time (Amman)</span>
          <input className="nl-input" type="time" value={form.send_time} onChange={(e) => set("send_time", e.target.value)} />
        </label>
      </div>

      <div className="bc-field">
        <span>Send to people who follow</span>
        <TopicPicker value={form.categories} onChange={(next) => set("categories", next)} />
      </div>

      <div className="bc-field">
        <span>Each week's issue</span>
        <div className="bc-when" role="radiogroup" aria-label="Approval">
          <button type="button" className="ui-pill" role="radio" aria-checked={form.approval === "required"} aria-pressed={form.approval === "required"}
            onClick={() => set("approval", "required")}>Waits for my approval</button>
          <button type="button" className="ui-pill" role="radio" aria-checked={form.approval === "auto"} aria-pressed={form.approval === "auto"}
            onClick={() => set("approval", "auto")}>Sends automatically</button>
        </div>
      </div>

      {error && <p className="bc-error" role="alert">{error}</p>}
      <div className="bc-new-actions">
        <span className="bc-size">{describe(form)}</span>
        <button type="button" className="nl-btn-secondary" onClick={onDone} disabled={busy}>Cancel</button>
        <button type="button" className="nl-btn-primary" onClick={save} disabled={busy || !ready}>
          {existing ? "Save changes" : "Turn on"}
        </button>
      </div>
    </section>
  );
}
